import React, { useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import MobileTabBar, { type MobileTab } from './MobileTabBar';
import MobileAccountSheet from './MobileAccountSheet';

/** Maps the current route to the active tab (null on non-destination routes). */
function activeTabForPath(pathname: string): MobileTab | null {
  if (pathname.startsWith('/timesheets')) return 'time';
  if (pathname.startsWith('/team-absences')) return 'away';
  if (pathname.startsWith('/dashboard')) return 'home';
  return null;
}

interface MobileBottomNavProps {
  /** Collapsed-sidebar offset width at ≥ sm (full-width below, where it overlays). */
  activeDrawerWidth: number;
}

/**
 * The global phone-width bottom navigation: the Home/Time/Away/Me tab bar pinned
 * to the viewport bottom, plus the "Me" account sheet. Rendered by the Shell on
 * every route at ≤ `MOBILE_NAV_MAX_WIDTH`. The active tab follows the route (and
 * highlights "Me" while the sheet is open). Page-specific fixed bars (e.g. the
 * timesheet action bar) sit above this via `MOBILE_TAB_BAR_HEIGHT`.
 */
const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ activeDrawerWidth }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);

  const active = accountOpen ? 'me' : activeTabForPath(pathname);

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 0, sm: `${activeDrawerWidth}px` },
          right: 0,
          bottom: 0,
          zIndex: theme.zIndex.appBar,
          backgroundColor: 'background.paper',
        }}
      >
        <MobileTabBar active={active} onMeClick={() => setAccountOpen(true)} />
      </Box>
      <MobileAccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} />
    </>
  );
};

export default MobileBottomNav;
